/**
 * EmptyState — Standardized empty state component
 * Shows when no data is available, with icon, headline, description, and CTA
 *
 * Props:
 *   icon: string (text symbol, default: '—')
 *   headline: string (required)
 *   description: string (optional)
 *   ctaLabel: string (optional, e.g., "Explore events")
 *   ctaOnClick: () => void (optional)
 *   theme: VB (from useTheme)
 */

import React from 'react';
import { SPACING, BORDER_RADIUS } from '../../theme/designTokens';

export default function EmptyState({
  icon = '—',
  headline,
  description,
  ctaLabel,
  ctaOnClick,
  theme: VB,
}) {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 240,
      padding: SPACING.xl,
      background: VB.darkCard,
      borderRadius: BORDER_RADIUS.large,
      border: `1px solid ${VB.borderDark}`,
      textAlign: 'center',
    }}>
      {/* Icon */}
      <div style={{
        fontSize: '48px',
        marginBottom: SPACING.lg,
        opacity: 0.3,
      }}>
        {icon}
      </div>

      {/* Headline */}
      <h3 style={{
        fontSize: '18px',
        fontWeight: 800,
        color: VB.snowPrimary,
        margin: `0 0 ${SPACING.sm}px 0`,
        maxWidth: 380,
      }}>
        {headline}
      </h3>

      {/* Description */}
      {description && (
        <p style={{
          fontSize: '14px',
          color: VB.snowMuted,
          margin: `0 0 ${SPACING.lg}px 0`,
          maxWidth: 380,
          lineHeight: 1.6,
        }}>
          {description}
        </p>
      )}

      {/* CTA Button */}
      {ctaLabel && ctaOnClick && (
        <button
          onClick={ctaOnClick}
          style={{
            padding: `${SPACING.sm}px ${SPACING.lg}px`,
            borderRadius: BORDER_RADIUS.compact,
            background: VB.red,
            color: '#FFFFFF',
            border: 'none',
            cursor: 'pointer',
            fontWeight: 700,
            fontSize: '14px',
            transition: 'opacity 150ms ease',
          }}
          onMouseEnter={(e) => { e.target.style.opacity = '0.85'; }}
          onMouseLeave={(e) => { e.target.style.opacity = '1'; }}
        >
          {ctaLabel} →
        </button>
      )}
    </div>
  );
}
